import { inject, Injectable } from "@angular/core";
import { Store } from "@ngxs/store";
import { CaseTypesState } from "@vp/data-access/case-types";
import { OrganizationState } from "@vp/data-access/organization";
import { CaseType, FeeBasedService, Organization } from "@vp/models";
import { AlertItem } from "@vp/shared/components/alert";
import { filterNullMap } from "@vp/shared/operators";
import { Observable } from "rxjs";
import { Store as RXJSStore } from "rxjs-observable-store";
import { map } from "rxjs/operators";

export const MODEL_CHANGED_MESSAGE: AlertItem = {
  id: "model-changed",
  type: "toast-info",
  title: "Data modified",
  message: "Changes have been made to the cost overrides. Don't forget to save."
};

const FEE_BASED_SERVICE_FEATURE_ID = "feeBasedServices";

export interface OverrideViewModel {
  caseTypeId: string;
  serviceCosts: EditServiceCostModel[];
}

export class OverrideModel {
  /**
   * Represents a services cost override model
   */
  constructor(
    public caseTypeId?: string,
    public serviceCosts: EditServiceCostModel[] = []
  ) {}
}

export interface EditServiceCostModel {
  friendlyId?: string;
  active?: boolean;
  fee?: number;
}

/**
 * Represents edit case cost or group data model
 */
export interface EditCaseCostModel {
  serviceCostOverrides: OverrideModel[];
}

export class EditCaseCostState {
  model: EditCaseCostModel = {
    serviceCostOverrides: []
  };
  errorMessages: AlertItem[] | null = null;
}

export interface StoreServiceResult {
  ok?: boolean;
  errors?: string | string[];
}

@Injectable()
export class EditServiceCostService extends RXJSStore<EditCaseCostState> {
  allCaseTypes$: Observable<CaseType[]> = inject(Store).select(CaseTypesState.allCaseTypes);
  organization$: Observable<Organization | null> = inject(Store).select(
    OrganizationState.organization
  );

  constructor() {
    super(new EditCaseCostState());
  }

  //TODO: Update to use new feature service
  allFeeBasedServices$ = this.organization$.pipe(
    filterNullMap(),
    map(
      (organization: Organization) =>
        organization.features.find(f => f.friendlyId === FEE_BASED_SERVICE_FEATURE_ID)
          ?.feeBasedServices
    )
  );

  feeBasedServicesEnabled$ = this.organization$.pipe(
    filterNullMap(),
    map((organization: Organization) => {
      const feature = organization.features.find(
        f => f.friendlyId === FEE_BASED_SERVICE_FEATURE_ID
      );
      if (feature && feature?.enabled) {
        return true;
      }
      return false;
    })
  );

  overrides$ = this.state$.pipe(map(state => state.model.serviceCostOverrides));

  errorMessages$ = this.state$.pipe(map(state => state.errorMessages));

  caseTypeLookup$ = (id: string): Observable<CaseType | null> => {
    return this.allCaseTypes$.pipe(
      map((allCaseTypes: CaseType[]) => {
        const found = allCaseTypes.find(caseType => caseType.caseTypeId === id);
        if (found) {
          return found;
        } else {
          return { caseTypeId: id, displayName: `[ ${id} ]` } as CaseType;
        }
      })
    );
  };

  feeBasedServiceLookup$ = (friendlyId: string): Observable<FeeBasedService | null> => {
    return this.allFeeBasedServices$.pipe(
      map((allFeeBasedServices: FeeBasedService[]) => {
        const found = allFeeBasedServices.find(
          feeBasedService => feeBasedService.friendlyId === friendlyId
        );
        if (found) {
          return found;
        } else {
          return {
            friendlyId: friendlyId,
            displayName: `[ ${friendlyId} ]`
          } as FeeBasedService;
        }
      })
    );
  };

  filteredCaseTypes$ = (existingOverrides: OverrideModel[]): Observable<CaseType[]> => {
    return this.allCaseTypes$.pipe(
      map((allCaseTypes: CaseType[]) =>
        allCaseTypes.filter(
          caseType =>
            existingOverrides.findIndex(
              override => override?.caseTypeId === caseType?.caseTypeId
            ) === -1
        )
      )
    );
  };

  filteredFeebasedServices$ = (
    existingFeeBasedServices: EditServiceCostModel[]
  ): Observable<FeeBasedService[]> => {
    return this.allFeeBasedServices$.pipe(
      map((allFeeBasedServices: FeeBasedService[]) => {
        if (existingFeeBasedServices) {
          return allFeeBasedServices.filter(
            feebasedService =>
              existingFeeBasedServices.findIndex(
                override => override.friendlyId === feebasedService.friendlyId
              ) === -1
          );
        } else {
          return allFeeBasedServices;
        }
      })
    );
  };

  setOverrides(costOverrides: OverrideModel[]) {
    if (costOverrides === undefined || costOverrides === null) {
      return;
    }
    this.patchState(costOverrides, "model", "serviceCostOverrides");
  }

  addOverride(updatedOverride: OverrideModel): StoreServiceResult {
    if (updatedOverride === undefined || updatedOverride === null) {
      return { ok: true };
    }
    const overrides = this.state.model.serviceCostOverrides;
    const index = overrides.findIndex(
      (override: OverrideModel) => override?.caseTypeId === updatedOverride?.caseTypeId
    );
    if (index === -1) {
      overrides.push(updatedOverride);
    } else {
      return { errors: "REPEATED_CASE_TYPE" };
    }
    this.patchState(overrides, "model", "serviceCostOverrides");
    this.setDataChangededMessage();
    return { ok: true };
  }

  updateOverride(updatedOverride: OverrideModel): StoreServiceResult {
    if (updatedOverride === undefined || updatedOverride === null) {
      return { ok: true };
    }
    const overrides = this.state.model.serviceCostOverrides;
    const index = overrides.findIndex(
      (override: OverrideModel) => override?.caseTypeId === updatedOverride?.caseTypeId
    );
    if (index > -1) {
      overrides.splice(index, 1, updatedOverride);
    } else {
      return { errors: "NOT_FOUND" };
    }
    this.patchState(overrides, "model", "serviceCostOverrides");
    this.setDataChangededMessage();
    return { ok: true };
  }

  deleteOverride(updatedOverride: OverrideModel): StoreServiceResult {
    if (updatedOverride === undefined || updatedOverride === null) {
      return { ok: true };
    }
    const overrides = this.state.model.serviceCostOverrides;
    const index = overrides.findIndex(
      (override: OverrideModel) => override?.caseTypeId === updatedOverride?.caseTypeId
    );
    if (index > -1) {
      overrides.splice(index, 1);
    } else {
      return { errors: "NOT_FOUND" };
    }
    this.patchState(overrides, "model", "serviceCostOverrides");
    this.setDataChangededMessage();
    return { ok: true };
  }

  deleteServiceOverride(
    updatedOverride: OverrideModel,
    removeService: EditServiceCostModel
  ): StoreServiceResult {
    if (updatedOverride === undefined || updatedOverride === null) {
      return { ok: true };
    }

    const overrides = this.state.model.serviceCostOverrides;
    const index = overrides.findIndex(
      (override: OverrideModel) => override?.caseTypeId === updatedOverride?.caseTypeId
    );
    if (index > -1) {
      overrides[index].serviceCosts = overrides[index].serviceCosts.filter(
        service => service?.friendlyId !== removeService?.friendlyId
      );
    } else {
      return { errors: "NOT_FOUND" };
    }

    this.patchState(overrides, "model", "serviceCostOverrides");
    this.setDataChangededMessage();
    return { ok: true };
  }

  private setDataChangededMessage(): void {
    const errorMessages = this.state.errorMessages ?? [];
    if (errorMessages.length === 0) {
      errorMessages.push(MODEL_CHANGED_MESSAGE);
      this.patchState(errorMessages, "errorMessages");
    }
  }
}
